import React, { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../../supabaseClient'
import Header from '../../components/Header'
import { useNavigate } from 'react-router-dom'

function Pagination({ page, totalItems, pageSize, onPageChange }) {
  const totalPages = Math.ceil(totalItems / pageSize)
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
      <span className="text-sm text-gray-600">
        {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
          style={{ color: page === 1 ? '#d1d5db' : '#374151', cursor: page === 1 ? 'not-allowed' : 'pointer' }}>
          ← Prev
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
          .reduce((acc, n, idx, arr) => {
            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
            acc.push(n)
            return acc
          }, [])
          .map((n, idx) =>
            n === "…" ? (
              <span key={`e-${idx}`} className="text-sm text-gray-400 px-1">…</span>
            ) : (
              <button key={n} onClick={() => onPageChange(n)}
                className="w-8 h-8 rounded-lg text-sm font-semibold"
                style={{
                  border: `1px solid ${n === page ? '#FF6F3C' : '#d1d5db'}`,
                  background: n === page ? '#FF6F3C' : '#fff',
                  color: n === page ? '#fff' : '#374151',
                  cursor: 'pointer',
                }}>
                {n}
              </button>
            )
          )
        }
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium"
          style={{ color: page === totalPages ? '#d1d5db' : '#374151', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>
          Next →
        </button>
      </div>
    </div>
  )
}

export default function StaffArchive({ onBack, onStaffRestored }) {
    const [archivedStaff, setArchivedStaff] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [filterRole, setFilterRole] = useState("All")
    const [showViewModal, setShowViewModal] = useState(false)
    const [viewingStaff, setViewingStaff] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 10
    const [stats, setStats] = useState({
        totalArchived: 0,
        archivedThisMonth: 0,
        archivedThisYear: 0
    })
    const navigate = useNavigate();

    useEffect(() => {
        const style = document.createElement('style')
        style.innerHTML = `
            .swal2-confirm {
                background-color: #FF6F3C !important;
                border: none !important;
            }
            .swal2-confirm:hover {
                background-color: #ff4c0b !important;
            }
            .swal2-cancel {
                background-color: #6b7280 !important;
                border: none !important;
            }
            .swal2-cancel:hover {
                background-color: #5a5a5a !important;
            }
        `
        document.head.appendChild(style)
        return () => document.head.removeChild(style)
    }, [])

    useEffect(() => {
        fetchArchivedStaff()
    }, [])

    useEffect(() => {
        setCurrentPage(1)
    }, [searchQuery, filterRole])

    const fetchArchivedStaff = async () => {
        try {
            setLoading(true)
            const { data, error } = await supabase
                .from('staff_archive')
                .select('*')
                .order('archived_at', { ascending: false })

            if (error) throw error

            setArchivedStaff(data || [])
            calculateStats(data || [])
        } catch (err) {
            console.error("Error fetching archived staff:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Load Archive",
                text: err.message || "An error occurred while loading archived staff",
                confirmButtonColor: "#A0826D"
            })
        } finally {
            setLoading(false)
        }
    }

    const calculateStats = (staffData) => {
        const now = new Date()
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        const startOfYear = new Date(now.getFullYear(), 0, 1)

        const totalArchived = staffData.length
        const archivedThisMonth = staffData.filter(s =>
            new Date(s.archived_at) >= startOfMonth
        ).length
        const archivedThisYear = staffData.filter(s =>
            new Date(s.archived_at) >= startOfYear
        ).length

        setStats({ totalArchived, archivedThisMonth, archivedThisYear })
    }

    const handleViewClick = (member) => {
        setViewingStaff(member)
        setShowViewModal(true)
    }

    const handleRestoreStaff = async (archiveId, staffName) => {
        const result = await Swal.fire({
            title: "Restore Branch Account?",
            html: `
        <p>Are you sure you want to restore <strong>${staffName}</strong>'s branch account to active status?</p>
        <p class="text-sm text-gray-600 mt-2">This will:</p>
        <ul class="text-sm text-gray-600 text-left mt-2 ml-8">
            <li>• Restore the branch account record</li>
            <li>• Recreate login access with password: <strong>Staff@123</strong></li>
            <li>• Remove it from the archive</li>
        </ul>
    `,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#10b981",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, restore",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            // Fetch the archived record's company_email so we can check for conflicts
            const { data: archiveRecord, error: archiveFetchError } = await supabase
                .from('staff_archive')
                .select('company_email, assigned_branch, role')
                .eq('id', archiveId)
                .single()

            if (archiveFetchError) throw archiveFetchError

            if (archiveRecord?.company_email) {
                const { data: existingActive, error: conflictCheckError } = await supabase
                    .from('staff')
                    .select('id')
                    .eq('company_email', archiveRecord.company_email)
                    .maybeSingle()

                if (conflictCheckError) throw conflictCheckError

                if (existingActive) {
                    // The original login email is already taken by another active account —
                    // generate a new unique one before restoring, same pattern used on Add.
                    const branchSlug = String(archiveRecord.assigned_branch || '')
                        .replace(/[^a-zA-Z\s]/g, '')
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)
                        .map(w => w[0])
                        .join('')
                        .toLowerCase()
                    const roleSlug = String(archiveRecord.role || '').toLowerCase().slice(0, 3)

                    let newCompanyEmail = `${branchSlug}-${roleSlug}@kaperk.com`
                    let suffix = 1
                    while (true) {
                        const { data: taken, error: takenError } = await supabase
                            .from('staff')
                            .select('id')
                            .eq('company_email', newCompanyEmail)
                            .maybeSingle()
                        if (takenError) throw takenError
                        if (!taken) break
                        suffix += 1
                        newCompanyEmail = `${branchSlug}-${roleSlug}-${suffix}@kaperk.com`
                    }

                    const { error: updateArchiveError } = await supabase
                        .from('staff_archive')
                        .update({ company_email: newCompanyEmail })
                        .eq('id', archiveId)

                    if (updateArchiveError) throw updateArchiveError
                }
            }

            const { data, error } = await supabase.rpc('restore_staff_member', {
                p_archive_id: archiveId
            })

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Branch Account Restored!",
                html: `
        <p>The branch account for ${staffName} has been successfully restored to active status</p>
        <p class="text-sm text-gray-600 mt-2">Default password: <strong>Staff@123</strong></p>
        <p class="text-xs text-gray-500">Password must be changed on first login</p>
    `,
                confirmButtonColor: "#A0826D",
                timer: 4000
            })

            fetchArchivedStaff()

            if (onStaffRestored) {
                onStaffRestored()
            }
        } catch (err) {
            console.error("Error restoring staff:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Restore",
                text: err.message || "An error occurred while restoring the staff member",
                confirmButtonColor: "#A0826D"
            })
        }
    }

    const handlePermanentDelete = async (archiveId, staffName) => {
        const result = await Swal.fire({
            title: "Permanently Delete?",
            html: `
                <p>Are you sure you want to <strong>permanently delete</strong> ${staffName}?</p>
                <p class="text-sm text-red-600 mt-2"><strong>Warning:</strong> This action cannot be undone!</p>
                <p class="text-sm text-gray-600 mt-2">All archive data will be lost forever.</p>
            `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, delete permanently",
            cancelButtonText: "Cancel"
        })

        if (!result.isConfirmed) return

        try {
            const { error } = await supabase
                .from('staff_archive')
                .delete()
                .eq('id', archiveId)

            if (error) throw error

            Swal.fire({
                icon: "success",
                title: "Permanently Deleted",
                text: `${staffName} has been permanently removed from the archive`,
                confirmButtonColor: "#A0826D",
                timer: 2000
            })

            // Close the View Details modal if it's open — otherwise it stays
            // rendered with a reference to the record that was just deleted.
            setShowViewModal(false)
            setViewingStaff(null)

            fetchArchivedStaff()
        } catch (err) {
            console.error("Error permanently deleting staff:", err)
            Swal.fire({
                icon: "error",
                title: "Failed to Delete",
                text: err.message || "An error occurred while deleting the archive record",
                confirmButtonColor: "#A0826D"
            })
        }
    }

    const filteredStaff = archivedStaff.filter(member => {
        const matchesSearch =
            member.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            member.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            member.email?.toLowerCase().includes(searchQuery.toLowerCase())

        const matchesRole = filterRole === "All" || member.role === filterRole

        return matchesSearch && matchesRole
    })

    const totalPages = Math.max(1, Math.ceil(filteredStaff.length / itemsPerPage))
    const paginatedStaff = filteredStaff.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    )
    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            <Header
                title="Branch Account Archive"
                subtitle="View and manage archived branch accounts"
                onGoToProfile={() => navigate('/profile')}
            />

            <div className="px-8 pt-4">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium transition"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8l8 8l1.41-1.41L7.83 13H20z" />
                    </svg>
                    Back to Branch Account Management
                </button>
            </div>

            <div className="p-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center text-white">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">Total Archived</p>
                                <p className="text-3xl font-bold text-gray-800">{stats.totalArchived}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-white">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M9 11H7v2h2zm4 0h-2v2h2zm4 0h-2v2h2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 16H5V9h14z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">This Month</p>
                                <p className="text-3xl font-bold text-gray-800">{stats.archivedThisMonth}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center text-white">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2m0 16H5V10h14zM9 14H7v-2h2zm4 0h-2v-2h2zm4 0h-2v-2h2zm-8 4H7v-2h2zm4 0h-2v-2h2zm4 0h-2v-2h2z" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm text-gray-600">This Year</p>
                                <p className="text-3xl font-bold text-gray-800">{stats.archivedThisYear}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Archive Table */}
                <div className="bg-white rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center gap-4">
                            <div className="relative flex-1 max-w-md">
                                <input
                                    type="text"
                                    placeholder="Search archived accounts..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                    </svg>
                                </span>
                            </div>
                        </div>

                        {/* Role Filter Tabs */}
                        <div className="flex gap-4 mt-4">
                            <button
                                onClick={() => setFilterRole('All')}
                                className={`px-4 py-2 rounded-lg font-medium ${filterRole === 'All'
                                    ? 'bg-[#FF6F3C] text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilterRole('Barista')}
                                className={`px-4 py-2 rounded-lg font-medium ${filterRole === 'Barista'
                                    ? 'bg-[#FF6F3C] text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Baristas
                            </button>
                            <button
                                onClick={() => setFilterRole('Admin')}
                                className={`px-4 py-2 rounded-lg font-medium ${filterRole === 'Admin'
                                    ? 'bg-[#FF6F3C] text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Admins
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Branch Account
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Branch
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Archived Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Reason
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            Loading archived staff...
                                        </td>
                                    </tr>
                                ) : filteredStaff.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                            No archived staff members found
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedStaff.map((member) => (
                                        <tr key={member.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>
                                                    <p className="font-medium text-gray-800">
                                                        {member.assigned_branch || "Unassigned"}
                                                    </p>
                                                    <p className="text-sm text-gray-500">{member.company_email}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${member.role === 'Barista'
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                    {member.role}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                {member.assigned_branch}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-gray-800">
                                                {new Date(member.archived_at).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                })}
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="text-sm text-gray-600 max-w-xs truncate" title={member.archive_reason}>
                                                    {member.archive_reason || 'No reason provided'}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleViewClick(member)}
                                                        className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                                                        title="View Details"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                                            <path fill="currentColor" d="M12 9a3 3 0 0 0-3 3a3 3 0 0 0 3 3a3 3 0 0 0 3-3a3 3 0 0 0-3-3m0 8a5 5 0 0 1-5-5a5 5 0 0 1 5-5a5 5 0 0 1 5 5a5 5 0 0 1-5 5m0-12.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handleRestoreStaff(member.id, `${member.first_name} ${member.last_name}`)}
                                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                        title="Restore Branch Account"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                                            <path fill="currentColor" d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89l.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7s-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18m-1 5v5l4.25 2.52l.77-1.28l-3.52-2.09V8z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => handlePermanentDelete(member.id, `${member.first_name} ${member.last_name}`)}
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                        title="Delete Permanently"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                                            <path fill="currentColor" d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {filteredStaff.length > 0 && (
                    <Pagination
                        page={currentPage}
                        totalItems={filteredStaff.length}
                        pageSize={itemsPerPage}
                        onPageChange={setCurrentPage}
                    />
                    )}
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && viewingStaff && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => {
                            setShowViewModal(false)
                            setViewingStaff(null)
                        }}
                    ></div>

                    <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-4 sm:px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Archived Branch Account Details</h2>
                                    <p className="text-sm text-gray-600">View complete archive information for this branch account</p>
                                </div>
                            </div>
                        </div>

                        <div className="px-4 sm:px-6 py-6 space-y-6">
                            {/* Archive Information */}
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <h3 className="text-sm font-semibold text-orange-800 mb-2">Archive Information</h3>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <span className="text-gray-600">Archived Date:</span>
                                        <p className="font-medium text-gray-800">
                                            {new Date(viewingStaff.archived_at).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-gray-600">Archive Reason:</span>
                                        <p className="font-medium text-gray-800">
                                            {viewingStaff.archive_reason || 'No reason provided'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Personal Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Branch</label>
                                    <p className="text-gray-800 font-medium">{viewingStaff.assigned_branch || "Unassigned"}</p>
                                </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Login Email</label>
                                        <p className="text-gray-800 font-medium">{viewingStaff.company_email}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Branch Owner Email</label>
                                        <p className="text-gray-800 font-medium">{viewingStaff.email}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Phone Number</label>
                                        <p className="text-gray-800 font-medium">{viewingStaff.phone_number}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Employment Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Employment Information</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">                                    <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
                                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${viewingStaff.role === 'Barista'
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-purple-100 text-purple-800'
                                        }`}>
                                        {viewingStaff.role}
                                    </span>
                                </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Branch</label>
                                        <p className="text-gray-800 font-medium">{viewingStaff.assigned_branch}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Start Date</label>
                                        <p className="text-gray-800 font-medium">
                                            {new Date(viewingStaff.start_date).toLocaleDateString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-600 mb-1">Employment Type</label>
                                        <p className="text-gray-800 font-medium">{viewingStaff.employment_type}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Permissions */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Permissions (At Time of Archive)</h3>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded flex items-center justify-center ${viewingStaff.permissions?.canViewAnalytics
                                            ? 'bg-green-100 text-green-600'
                                            : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {viewingStaff.permissions?.canViewAnalytics ? '✓' : '✕'}
                                        </div>
                                        <span className="text-sm text-gray-700">Can view analytics dashboard</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded flex items-center justify-center ${viewingStaff.permissions?.canManageOrders
                                            ? 'bg-green-100 text-green-600'
                                            : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {viewingStaff.permissions?.canManageOrders ? '✓' : '✕'}
                                        </div>
                                        <span className="text-sm text-gray-700">Can manage orders</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded flex items-center justify-center ${viewingStaff.permissions?.canAccessCustomerData
                                            ? 'bg-green-100 text-green-600'
                                            : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {viewingStaff.permissions?.canAccessCustomerData ? '✓' : '✕'}
                                        </div>
                                        <span className="text-sm text-gray-700">Can access customer data</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-5 h-5 rounded flex items-center justify-center ${viewingStaff.permissions?.canModifyInventory
                                            ? 'bg-green-100 text-green-600'
                                            : 'bg-gray-100 text-gray-400'
                                            }`}>
                                            {viewingStaff.permissions?.canModifyInventory ? '✓' : '✕'}
                                        </div>
                                        <span className="text-sm text-gray-700">Can modify inventory</span>
                                    </div>
                                </div>
                            </div>
                        </div>
<div className="border-t border-gray-200 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:justify-between gap-3">
                            <button
                                onClick={() => handlePermanentDelete(viewingStaff.id, `${viewingStaff.first_name} ${viewingStaff.last_name}`)}
                                className="w-full sm:w-auto px-6 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 order-3 sm:order-1"
                            >
                                Delete Permanently
                            </button>
                            <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2">
                                <button
                                    onClick={() => {
                                        setShowViewModal(false)
                                        setViewingStaff(null)
                                    }}
                                    className="w-full sm:w-auto px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        setShowViewModal(false)
                                        handleRestoreStaff(viewingStaff.id, `${viewingStaff.first_name} ${viewingStaff.last_name}`)
                                        setViewingStaff(null)
                                    }}
                                    className="w-full sm:w-auto px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
                                >
                                    Restore Account
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}